import { cn } from '@client/utils/styling/cn';
import { Spin, type SpinProps } from 'antd';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import { Spinner } from './Spinner';

type LoadingSize = 'small' | 'medium' | 'large';

/** Standalone spinners wait this long before appearing, so a quick load shows nothing at all. */
const STANDALONE_DELAY_MS = 200;

export interface LoadingProps extends Omit<SpinProps, 'size' | 'children'> {
    /** Spinner size. small = 16px, medium = 24px, large = 32px */
    size?: LoadingSize;
    /** When provided, renders in overlay mode — spinner appears over children. */
    children?: ReactNode;
    /**
     * Overlay mode only: keep the `Spin` wrapper in the DOM while `spinning` is false, as a raw antd
     * `Spin` does. Without it an idle overlay renders its children bare, so toggling `spinning`
     * remounts them and drops their local state; set it when the children hold state (a form, a
     * table, a scroll position) or when the wrapper's layout must not change between the two states.
     */
    keepWrapper?: boolean;
}

/**
 * Unified loading spinner wrapper for Ant Design `Spin`.
 *
 * Two modes:
 * - **Standalone** (no children): renders a centred spinner filling the parent's height, after a
 *   short `delay` so a quick load shows nothing. In a content area with no height of its own (a tab
 *   panel, an empty section) pass `className="min-h-60"`, so the spinner sits in the space instead of
 *   on its top edge.
 * - **Overlay** (with children): wraps content with a spinner overlay.
 *
 * When `spinning` is explicitly `false` in overlay mode, children are rendered
 * directly without any Spin wrapper DOM, unless `keepWrapper` is set.
 *
 * Extends `SpinProps` — all native props (`indicator`, `delay`, `fullscreen`, etc.)
 * pass through to Ant Design `Spin`.
 *
 * Where the layout that is loading is known (a dashboard, a table, a form), prefer a
 * `SkeletonBlock` shaped like it.
 */
export const Loading = forwardRef<HTMLDivElement, LoadingProps>(
    ({ size = 'medium', children, className, classNames, description, keepWrapper = false, ...spinProps }, ref) => {
        // A description already says what is loading, so the spinner does not announce it twice. antd
        // shows `description ?? tip`, so the legacy `tip` alias counts as a description too.
        const indicator = <Spinner indicator size={size} label={(description ?? spinProps.tip) ? null : undefined} />;

        // Overlay mode: wrap children with Spin
        if (children !== undefined) {
            // When explicitly not spinning, render children directly (no wrapper DOM).
            // This preserves the useSpin contract where idle = no wrapper; `keepWrapper` opts out.
            if (spinProps.spinning === false && !keepWrapper) {
                return <>{children}</>;
            }
            return (
                <Spin
                    spinning={spinProps.spinning ?? true}
                    size={size}
                    indicator={indicator}
                    className={className}
                    classNames={classNames}
                    description={description}
                    {...spinProps}
                >
                    {children}
                </Spin>
            );
        }

        // Standalone mode: centred spinner. Childless (non-nested) Spin so the
        // description keeps its natural width and stays inside Spin's aria-live
        // region — the old empty-child nesting constrained the description to
        // the empty content's width, wrapping it into an unreadable column.
        const standaloneClassNames: SpinProps['classNames'] =
            typeof classNames === 'function'
                ? (info) => {
                      const resolvedClassNames = classNames(info);
                      return {
                          ...resolvedClassNames,
                          description: cn('mt-3 text-center text-muted-foreground', resolvedClassNames?.description),
                      };
                  }
                : {
                      ...classNames,
                      description: cn('mt-3 text-center text-muted-foreground', classNames?.description),
                  };

        return (
            <div ref={ref} className={cn('flex h-full items-center justify-center', className)}>
                <Spin
                    spinning
                    size={size}
                    delay={STANDALONE_DELAY_MS}
                    indicator={indicator}
                    description={description}
                    classNames={standaloneClassNames}
                    {...spinProps}
                />
            </div>
        );
    },
);

Loading.displayName = 'Loading';

export type { LoadingSize };
